import { cn } from "@newxsystems/shared";
import { TootActions } from "../../../../modules/layout/components/TootActions/TootActions";
import { TootHeading } from "../../../../modules/layout/components/TootHeading/TootHeading";
import { TootSectionShell } from "../../../../modules/layout/components/TootSectionShell/TootSectionShell";
import { TootMediaFrame } from "../../../../modules/surface/components/TootMediaFrame/TootMediaFrame";
import { TootTechPanel } from "../../../../modules/surface/components/TootTechPanel/TootTechPanel";
import type { ITextMediaBlockProps } from "./TextMediaBlock.props";
import { RichTextHtml } from "../../RichTextHtml";

export const TextMediaBlock = ({
  anchorId,
  buttons,
  direction,
  eyebrow,
  heading,
  media,
  parentClass,
  spacing,
  subheading,
}: ITextMediaBlockProps) => {
  const isRtl = direction === "rtl";
  const actions = buttons ?? [];
  const image = media?.type === "image" ? media.image : undefined;
  const video = media?.type === "video" ? media.video : undefined;
  const sectionId = anchorId ?? undefined;
  const hasText = Boolean(eyebrow || heading || subheading || actions.length > 0);
  const hasMedia = Boolean(image?.image.defaultSrc || video?.embedUrl);

  if (!hasText && !hasMedia) {
    return null;
  }

  return (
    <TootSectionShell
      className={cn(
        "grid items-center gap-8 lg:gap-12",
        hasText && hasMedia
          ? isRtl
            ? "lg:grid-cols-[minmax(0,1.08fr)_minmax(0,0.92fr)]"
            : "lg:grid-cols-[minmax(0,0.92fr)_minmax(0,1.08fr)]"
          : "mx-auto max-w-4xl",
        hasText && isRtl && "lg:[&>*:first-child]:order-2",
      )}
      id={sectionId}
      rootClassName={parentClass}
      spacing={spacing ?? undefined}
      width="wide"
    >
      {hasText ? (
        <div className="relative">
          {heading ? (
            <TootHeading eyebrow={eyebrow} heading={heading} subheading={subheading} />
          ) : eyebrow || subheading ? (
            <div className={cn("toot-heading-stack", "items-start text-left")}>
              {eyebrow ? <p className="toot-eyebrow">{eyebrow}</p> : null}
              {subheading ? <RichTextHtml className="toot-subheading" html={subheading} /> : null}
            </div>
          ) : null}
          {actions.length > 0 ? (
            <TootActions actions={actions} alignment="left" className="mt-7" />
          ) : null}
        </div>
      ) : null}

      {hasMedia ? (
        <TootTechPanel
          className="rounded-[var(--toot-radius-card)] border p-3 md:p-4"
          showDividers={false}
          variant="media"
        >
          <div className="relative">
            <div className="grid gap-4">
              {image?.image.defaultSrc ? (
                <TootMediaFrame
                  alt={image.image.alternateText}
                  caption={image.caption}
                  className="border-toot-grid-strong/65 bg-toot-surface-terminal/80 shadow-[0_18px_55px_rgba(0,0,0,0.32)]"
                  focalPoint={image.image.focalPoint ?? undefined}
                  height={image.image.height ?? undefined}
                  kind="image"
                  sizes={image.image.sizes ?? undefined}
                  src={image.image.defaultSrc}
                  srcSet={image.image.srcSet ?? undefined}
                  width={image.image.width ?? undefined}
                />
              ) : video?.embedUrl ? (
                <TootMediaFrame
                  aspectRatio={video.aspectRatio ?? undefined}
                  caption={video.caption}
                  className="border-toot-grid-strong/65 bg-toot-surface-terminal/80 shadow-[0_18px_55px_rgba(0,0,0,0.32)]"
                  embedTitle={video.title}
                  focalPoint={video.poster?.focalPoint ?? undefined}
                  height={video.poster?.height ?? undefined}
                  kind="embed"
                  playOnPosterClick={Boolean(video.poster?.defaultSrc)}
                  poster={video.poster?.defaultSrc}
                  posterAlt={video.poster?.alternateText}
                  src={video.embedUrl}
                  srcSet={video.poster?.srcSet ?? undefined}
                  width={video.poster?.width ?? undefined}
                />
              ) : null}
            </div>
          </div>
        </TootTechPanel>
      ) : null}
    </TootSectionShell>
  );
};

TextMediaBlock.displayName = "Block::TextMediaBlock";
